import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { SelectBox } from 'devextreme-react/select-box';
import CheckBox from 'devextreme-react/check-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import DataGrid, { Column, Paging } from 'devextreme-react/data-grid';
import { alert } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { getApiErrorMessage, CONCURRENCY_HANDLED } from '../../../../common/concurrency';
import { MESSAGES } from '../../../../common/Messages';
import {
    SLOT_ACTION,
    SLOT_STATUS,
    SLOT_STATUS_CLASS,
    SLOT_STATUS_LABELS,
} from './constants';
import { GetPickCapacityDay, ApplyToSlot, GetCarriers } from './services/PickCapacityService';
import './pickCapacity.css';

// Pick Capacity Slots — replaces the carrier Apply flow on the WM - Carrier - Scheduling page.
// The Driver Reference and Same Trailer inputs that used to sit on that page's toolbar now live in
// this popup's header, and APPLY on a slot row runs the same ApplySchedule flow with the slot's
// date/time in place of the free-typed one.
//
// PROPERTY CASING: camelCase — PickCapacityController returns through plain Ok(...), so the global
// camelCase naming applies. Match the controller, not a neighbouring screen.
//
// The component only renders what the server sent: which slots appear, the figures and whether a row
// offers APPLY are all decided in PickCapacityService.
function PickCapacitySlotsDialog(props) {
    const {
        visible,
        onHide,
        onApplied,
        accessToken,
        selectedKeys,
        driverReferenceDataSource,
    } = props;

    const [loading, setLoading] = useState(false);
    const [day, setDay] = useState(null);
    const [payload, setPayload] = useState(null);
    const [driverReference, setDriverReference] = useState('');
    const [isSameTrailer, setIsSameTrailer] = useState(false);
    const [carriers, setCarriers] = useState([]);
    const [carrierId, setCarrierId] = useState(null);

    // Guards against a slow response for a day the user has already navigated away from.
    const requestSeq = useRef(0);

    const slots = payload?.slots || [];

    const load = useCallback((targetDay) => {
        if (!selectedKeys || !selectedKeys.length) return;

        const seq = ++requestSeq.current;
        setLoading(true);

        GetPickCapacityDay(accessToken, selectedKeys, targetDay)
            .then((res) => {
                if (seq !== requestSeq.current) return;
                setPayload(res.data);

                // Adopt the day the server resolved. Guarded against a year-1 value: a default
                // DateTime serializes as "0001-01-01T00:00:00" and would render as 1/1/1.
                const resolved = res.data?.day ? new Date(res.data.day) : null;
                if (resolved && !isNaN(resolved) && resolved.getFullYear() > 1900) {
                    setDay(resolved);
                }
            })
            .catch((err) => {
                if (err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
            })
            .finally(() => {
                if (seq === requestSeq.current) setLoading(false);
            });
    }, [accessToken, selectedKeys]);

    // Re-read on open. A different selection changes both the facility and the shipments being
    // booked, so the popup always starts from the server's view rather than stale state.
    useEffect(() => {
        if (!visible) return;
        setPayload(null);
        setDriverReference('');
        setIsSameTrailer(false);
        setCarrierId(null);
        load(null);
    }, [visible, load]);

    // Fetched once per mount, and only when the server says this user holds the Override Carrier
    // Code permission — the carrier list does not change while the popup is open.
    useEffect(() => {
        if (!visible || !payload?.canOverrideCarrierCode || carriers.length) return;

        GetCarriers(accessToken)
            .then((res) => setCarriers(Array.isArray(res.data) ? res.data : []))
            .catch(() => {
                // A missing list must not block booking a slot: the carrier field is optional, so the
                // dropdown simply stays empty and the shipment keeps the carrier it already has.
                setCarriers([]);
            });
    }, [visible, payload, carriers.length, accessToken]);

    const onDayChanged = useCallback((e) => {
        setDay(e.value);
        load(e.value);
    }, [load]);

    const onRefresh = useCallback(() => load(day), [load, day]);

    // Days the facility does not operate, and All Day exception dates, cannot be navigated to.
    const disabledDates = useMemo(() => {
        if (!payload?.disabledDays) return [];
        return payload.disabledDays.map((d) => new Date(d));
    }, [payload]);

    const onApply = useCallback((slot) => {
        setLoading(true);

        ApplyToSlot(accessToken, {
            keys: selectedKeys,
            slotDateTime: slot.slotDateTime,
            isSameTrailer,
            fmDriverRef: driverReference || null,
            // Left null unless the user actually picked one, so applying a slot never disturbs the
            // carrier already on the shipment.
            assignCarrierId: carrierId || null,
        })
            .then((res) => {
                notify(res.data?.message || MESSAGES.SCHEDULING_ACTION_COMPLETE, 'success', 3000);
                if (onApplied) onApplied();
                onHide();
            })
            .catch((err) => {
                if (err?.[CONCURRENCY_HANDLED]) return;
                // A slot that filled or closed since the popup loaded lands here; re-read so the
                // figures on screen are current.
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
                load(day);
            })
            .finally(() => setLoading(false));
    }, [accessToken, selectedKeys, isSameTrailer, driverReference, carrierId, onApplied, onHide, load, day]);

    // -- Cell renderers ---------------------------------------------------------------------------

    const renderNumber = useCallback((cell) => (
        cell.value == null ? <span>&mdash;</span> : <span>{cell.value}</span>
    ), []);

    const renderStatus = useCallback((cell) => {
        const slot = cell.data;
        return (
            <div>
                <span className={SLOT_STATUS_CLASS[slot.status] || ''}>
                    {SLOT_STATUS_LABELS[slot.status] || slot.status}
                </span>
                {slot.status === SLOT_STATUS.UNAVAILABLE && slot.unavailableReason && (
                    <div className="pick-capacity-unavailable-reason">{slot.unavailableReason}</div>
                )}
            </div>
        );
    }, []);

    const renderAction = useCallback((cell) => {
        const slot = cell.data;
        const isRequest = slot.action === SLOT_ACTION.REQUEST;

        // The server decides which of the three a slot offers; this only renders it. REQUEST appears
        // when the selection does not fit and the user may ask rather than book outright.
        if (slot.action !== SLOT_ACTION.APPLY && !isRequest) {
            return <span className="pick-capacity-unavailable-reason">{slot.unavailableReason || ''}</span>;
        }

        return (
            <Button
                text={`${slot.timeSlot}  ${isRequest ? 'REQUEST' : 'APPLY'}`}
                type={isRequest ? 'normal' : 'default'}
                stylingMode="contained"
                onClick={() => onApply(slot)}
            />
        );
    }, [onApply]);

    // -- Render -----------------------------------------------------------------------------------

    return (
        <Popup
            visible={visible}
            onHiding={onHide}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Pick Capacity Slots (WM-CARRIER-SCHED)"
            width="90%"
            height="85%"
        >
            <LoadPanel visible={loading} shadingColor="rgba(0,0,0,0.1)" />

            <div className="pick-capacity-header">
                <div className="pick-capacity-field">
                    <span className="pick-capacity-field-label">Day</span>
                    <DateBox
                        type="date"
                        value={day}
                        onValueChanged={onDayChanged}
                        min={payload?.minDay ? new Date(payload.minDay) : undefined}
                        max={payload?.maxDay ? new Date(payload.maxDay) : undefined}
                        disabledDates={disabledDates}
                        width={160}
                    />
                </div>

                {/* Carrier code. Optional — the shipment keeps its existing carrier unless one is
                    chosen here. Shown only to a role holding Override Carrier Code; the service
                    enforces the same permission when APPLY posts. */}
                {payload?.canOverrideCarrierCode && (
                    <div className="pick-capacity-field">
                        <span className="pick-capacity-field-label">Carrier code</span>
                        <SelectBox
                            dataSource={carriers}
                            value={carrierId}
                            displayExpr="name"
                            valueExpr="id"
                            searchEnabled={true}
                            showClearButton={true}
                            placeholder="Keep existing"
                            onValueChanged={(e) => setCarrierId(e.value || null)}
                            width={240}
                        />
                    </div>
                )}

                {/* Driver Reference and Same Trailer, moved here from the Scheduling toolbar. The
                    reference is optional — ApplySchedule generates one when it is left blank. */}
                <div className="pick-capacity-field">
                    <span className="pick-capacity-field-label">Driver Reference</span>
                    <SelectBox
                        dataSource={driverReferenceDataSource}
                        value={driverReference}
                        displayExpr="name"
                        valueExpr="name"
                        searchEnabled={false}
                        showClearButton={true}
                        placeholder="Auto-generate"
                        onValueChanged={(e) => setDriverReference(e.value || '')}
                        width={200}
                    />
                </div>

                <div className="pick-capacity-field">
                    <span className="pick-capacity-field-label">&nbsp;</span>
                    <div className="pick-capacity-facility">
                        <CheckBox
                            text="Same Trailer"
                            value={isSameTrailer}
                            onValueChanged={(e) => setIsSameTrailer(e.value)}
                        />
                    </div>
                </div>

                <Button icon="refresh" text="Refresh" onClick={onRefresh} />

                {/* Week and Exception Settings are per facility, so the popup says whose calendar
                    is on screen — otherwise the hours and capacity have no visible owner. */}
                <div className="pick-capacity-field">
                    <span className="pick-capacity-field-label">Warehouse</span>
                    <div className="pick-capacity-facility">
                        {payload?.warehouseCode
                            ? <><strong>{payload.warehouseCode}</strong>{payload.warehouseName ? ` — ${payload.warehouseName}` : ''}</>
                            : <span className="pick-capacity-unavailable-reason">Not resolved</span>}
                    </div>
                </div>

                <div className="pick-capacity-summary">
                    <div>Selected shipments: <strong>{payload?.shipments?.length || 0}</strong></div>
                    <div>Picks required: <strong>{payload?.selectedPicks ?? 0}</strong></div>
                </div>
            </div>

            {payload && payload.isDayAvailable === false ? (
                <div className="pick-capacity-day-closed">
                    {payload.unavailableReason || MESSAGES.SCHEDULING_DAY_UNAVAILABLE}
                </div>
            ) : (
                <DataGrid
                    dataSource={slots}
                    keyExpr="slotDateTime"
                    showBorders={true}
                    hoverStateEnabled={true}
                    columnAutoWidth={true}
                    height="calc(100% - 90px)"
                >
                    <Paging enabled={false} />

                    <Column dataField="timeSlot" caption="Time Slot" width={110} allowSorting={false} />
                    <Column dataField="capacity" caption="Capacity" width={110} alignment="right" allowSorting={false} cellRender={renderNumber} />
                    <Column dataField="scheduled" caption="Scheduled" width={110} alignment="right" allowSorting={false} cellRender={renderNumber} />
                    <Column dataField="remaining" caption="Remaining" width={110} alignment="right" allowSorting={false} cellRender={renderNumber} />
                    <Column dataField="requests" caption="Requests" width={100} alignment="right" allowSorting={false} />
                    <Column dataField="status" caption="Status" width={170} allowSorting={false} cellRender={renderStatus} />
                    <Column caption="Action" allowSorting={false} cellRender={renderAction} />
                </DataGrid>
            )}
        </Popup>
    );
}

export default PickCapacitySlotsDialog;
